import { useEffect, useState } from "react";
import type { CostInsight } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { useConnectionContext } from "@/lib/connectionContext";
import { ChartLegend, HourlyChart, type HourlySeries } from "@/components/charts/HourlyChart";

type Loadable<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "error"; message: string };

/**
 * Insights → Cost. "Where is my Twilio spend going?"
 * Summary cards, hourly stacked spend (msgs + calls), by-connection table.
 */
export function CostPage() {
  const ctx = useConnectionContext();
  const scope = ctx.allSelected ? undefined : ctx.selectedIds;
  const scopeKey = scope ? scope.join(",") : "all";

  const [insight, setInsight] = useState<Loadable<CostInsight>>({ status: "loading" });

  useEffect(() => {
    if (ctx.status !== "ready") return;
    let cancelled = false;
    setInsight({ status: "loading" });
    void api.insights
      .cost(scope)
      .then((data) => {
        if (!cancelled) setInsight({ status: "ready", data });
      })
      .catch((err) => {
        if (!cancelled) {
          setInsight({
            status: "error",
            message: err instanceof ApiClientError ? err.message : "Failed to load",
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, scopeKey]);

  return (
    <div className="space-y-8">
      <header>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
          Insights
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Cost
        </h1>
        <p className="mt-1 text-[13.5px] text-zinc-600 dark:text-zinc-400">
          {scopeLabel(ctx)} · Last 24 hours.
        </p>
      </header>

      {insight.status === "loading" && <LoadingState />}
      {insight.status === "error" && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {insight.message}
        </p>
      )}
      {insight.status === "ready" && insight.data.summary.total === 0 && <EmptyState />}
      {insight.status === "ready" && insight.data.summary.total > 0 && (
        <>
          <SummaryRow data={insight.data} />
          <SpendChart data={insight.data} />
          <ByConnectionTable data={insight.data} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function SummaryRow({ data }: { data: CostInsight }) {
  const s = data.summary;
  const cur = (s.currency ?? "USD").toUpperCase();
  const cards: Array<{ label: string; value: string; hint: string }> = [
    {
      label: "Spend · 24h",
      value: formatMoney(s.total, cur),
      hint: "Messages + calls combined",
    },
    {
      label: "Messages · 24h",
      value: formatMoney(s.messagesSpend, cur),
      hint: shareLabel(s.messagesSpend, s.total),
    },
    {
      label: "Calls · 24h",
      value: formatMoney(s.callsSpend, cur),
      hint: shareLabel(s.callsSpend, s.total),
    },
    {
      label: "Projected · monthly",
      value: formatMoney(s.projectedMonthly, cur),
      hint: "24h × 30 — naive run-rate",
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none p-4 dark:ring-1 dark:ring-inset dark:ring-white/[0.02]"
        >
          <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
            {c.label}
          </div>
          <div className="mt-1.5 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            {c.value}
          </div>
          <div className="mt-1 text-[11.5px] text-zinc-500">{c.hint}</div>
        </div>
      ))}
    </div>
  );
}

function shareLabel(part: number, total: number): string {
  if (total <= 0) return "—";
  return `${Math.round((part / total) * 100)}% of spend`;
}

// ---------------------------------------------------------------------------

function SpendChart({ data }: { data: CostInsight }) {
  const cur = (data.summary.currency ?? "USD").toUpperCase();
  const series: HourlySeries[] = [
    {
      label: "Messages",
      fill: "rgb(99 102 241 / 0.85)",
      values: data.hourly.map((b) => b.messagesSpend),
    },
    {
      label: "Calls",
      fill: "rgb(34 211 238 / 0.85)",
      values: data.hourly.map((b) => b.callsSpend),
    },
  ];
  return (
    <section className="rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none p-5 dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <header className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-[13.5px] font-semibold text-zinc-900 dark:text-zinc-100">
            Hourly spend · last 24h
          </h2>
          <p className="text-[11.5px] text-zinc-500">Stacked: messages + calls · {cur}.</p>
        </div>
        <ChartLegend series={series} />
      </header>
      <HourlyChart
        hours={data.hourly.map((b) => b.hour)}
        series={series}
        valueFormatter={(n) => `${cur} ${n.toFixed(n < 1 ? 4 : 2)}`}
        axisFormatter={(n) => (n === 0 ? "0" : `${cur} ${n.toFixed(n < 1 ? 2 : 0)}`)}
        ariaLabel="Hourly spend over the last 24 hours"
      />
    </section>
  );
}

// ---------------------------------------------------------------------------

function ByConnectionTable({ data }: { data: CostInsight }) {
  const cur = (data.summary.currency ?? "USD").toUpperCase();
  if (data.byConnection.length === 0) return null;
  return (
    <section>
      <header className="mb-2">
        <h2 className="text-[13.5px] font-semibold text-zinc-900 dark:text-zinc-100">
          By connection
        </h2>
        <p className="text-[11.5px] text-zinc-500">
          Spend grouped by Twilio account · sorted by total.
        </p>
      </header>
      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
        <table className="w-full text-left text-sm">
          <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider dark:bg-zinc-900/60 text-zinc-500">
            <tr>
              <th className="px-4 py-2.5">Account</th>
              <th className="px-4 py-2.5 text-right">Messages</th>
              <th className="px-4 py-2.5 text-right">Calls</th>
              <th className="px-4 py-2.5 text-right">Total</th>
              <th className="px-4 py-2.5">Share</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
            {data.byConnection.map((c) => (
              <tr
                key={c.connectionId}
                className="transition hover:bg-zinc-50 dark:hover:bg-zinc-900/60"
              >
                <td className="px-4 py-3 align-middle text-[12.5px] text-zinc-900 dark:text-zinc-100">
                  {c.connectionName}
                </td>
                <td className="px-4 py-3 align-middle text-right font-mono text-[12px] text-zinc-700 dark:text-zinc-300">
                  {formatMoney(c.messagesSpend, cur)}
                </td>
                <td className="px-4 py-3 align-middle text-right font-mono text-[12px] text-zinc-700 dark:text-zinc-300">
                  {formatMoney(c.callsSpend, cur)}
                </td>
                <td className="px-4 py-3 align-middle text-right font-mono text-[12.5px] text-zinc-900 dark:text-zinc-50">
                  {formatMoney(c.total, cur)}
                </td>
                <td className="px-4 py-3 align-middle">
                  <SharePill share={c.share} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SharePill({ share }: { share: number }) {
  const pct = Math.round(share * 100);
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-1.5 w-20 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
        <span
          className="block h-full bg-brand-500 dark:bg-brand-400/80"
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </span>
      <span className="font-mono text-[11.5px] text-zinc-700 dark:text-zinc-300">{pct}%</span>
    </span>
  );
}

// ---------------------------------------------------------------------------

function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-[88px] animate-pulse rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none"
          />
        ))}
      </div>
      <div className="h-[200px] animate-pulse rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-10 text-center dark:border-zinc-800 dark:bg-zinc-900/30">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
        No spend in the last 24 hours
      </h2>
      <p className="mx-auto mt-1.5 max-w-md text-[13px] text-zinc-600 dark:text-zinc-400">
        Spend lights up here once messages or calls flow through your connected Twilio accounts.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function scopeLabel(ctx: ReturnType<typeof useConnectionContext>): string {
  if (ctx.connections.length === 0) return "No accounts connected yet";
  if (ctx.allSelected)
    return `Across all ${ctx.connections.length} ${ctx.connections.length === 1 ? "account" : "accounts"}`;
  if (ctx.selectedIds.length === 1) {
    const c = ctx.connections.find((x) => x.id === ctx.selectedIds[0]);
    return `Scoped to ${c?.friendlyName ?? c?.accountSid ?? "1 account"}`;
  }
  return `Scoped to ${ctx.selectedIds.length} of ${ctx.connections.length} accounts`;
}

function formatMoney(n: number, currency: string): string {
  if (n === 0) return `${currency} 0.00`;
  if (n < 1) return `${currency} ${n.toFixed(4)}`;
  return `${currency} ${n.toFixed(2)}`;
}
